import { map } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox } from '@/components/ui'
import { Plus, Trash } from 'lucide-react'
import { MediaPhotoCreateModal } from './MediaPhotoCreateModal'
import Loader from '../../../common/components/Loader'
import { Photo } from '../../../common/models'
import { IColumn, Table } from '../../../core/components/Table'
import { sanitizeDate } from '../../../core/constants/times'
import { useTableParams } from '../../../core/hooks/table-params'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import {
  deletePhoto,
  getPhotos,
  publishPhoto,
  unpublishPhoto,
} from '../../../core/services/video'
import { useAppSelector } from '../../../store'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { Pagination } from '../Pagination'
import { Icon } from '@/common/components/Icon'
import { ActionsMenu } from '@/components/ActionsMenu'

export const MediaPhotoPage: React.FC = (): JSX.Element => {
  const {
    page,
    setPage,
    size,
    setSize,
    totalElements,
    setTotalElemenets,
    sortBy,
    setSortBy,
  } = useTableParams()
  const { hasEditPermission } = useCheckPermission({ page: 'PHOTO_GALLERY' })
  const [photos, setPhotos] = useState<Photo[]>([])
  const [currentPhoto, setCurrentPhoto] = useState<Photo>()
  const [showPhotoCreateModal, setShowPhotoCreateModal] =
    useState<boolean>(false)

  const [isCheckAll, setIsCheckAll] = useState(false)
  const [isCheck, setIsCheck] = useState<string[]>([])
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)

  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const { festivalName, editionId } = useParams()

  const { festivals: storeFestivals } = useAppSelector((s) => s.app)

  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name

  const getData = useCallback(async () => {
    try {
      setLoading(true)
      const response = await getPhotos(editionId as string, {
        page,
        size,
        sortBy,
        sortDirection,
      })
      setTotalElemenets(response.data.totalElements)
      setPhotos(response.data.content)
      setSize(response.data.size)
      setPage(response.data.pageNumber + 1)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [
    editionId,
    page,
    size,
    sortBy,
    sortDirection,
    setPage,
    setSize,
    setTotalElemenets,
    festivalName,
  ])

  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      if (sortDirection === 'DESC') {
        setSortDirection('ASC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('DESC')
      setSortBy(sort)
    }
  }

  useEffect(() => {
    getData()
  }, [getData])

  const handleDelete = () => {
    if (!hasEditPermission) {
      return
    }
    if (isCheck.length > 0) {
      setDeleteConfirmationModal(true)
    }
  }

  const handleSelectAll = () => {
    if (!hasEditPermission) {
      return
    }
    setIsCheckAll(!isCheckAll)
    setIsCheck(map(photos, (photo) => photo.id))
    if (isCheckAll) {
      setIsCheck([])
    }
  }

  const handleClick = (id: string, checked: boolean) => {
    if (!hasEditPermission) {
      return
    }
    setIsCheck([...isCheck, id])
    if (!checked) {
      setIsCheck(isCheck.filter((item) => item !== id))
    }
  }

  const deleteFunc = () => {
    // prettier-ignore
    (async () => {
      setLoading(true)
      try {
        const { status } = await deletePhoto(editionId as string, isCheck)
        getData()
        setIsCheck([])
        setIsCheckAll(false)
        if (status === 204) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Delete Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }

  const handlePublish = (isCheck: string[]) => {
    if (!hasEditPermission) {
      return
    }
    // prettier-ignore
    (async () => {
      setLoading(true)
      try {
        const { status } = await publishPhoto(editionId as string, isCheck)
        getData()
        setIsCheck([])
        setIsCheckAll(false)
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Publish Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }

  const handleUnpublish = (isCheck: string[]) => {
    if (!hasEditPermission) {
      return
    }
    // prettier-ignore
    (async () => {
      setLoading(true)
      try {
        const { status } = await unpublishPhoto(editionId as string, isCheck)
        getData()
        setIsCheck([])
        setIsCheckAll(false)
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Unpublish Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }

  const columns: IColumn[] = [
    {
      title: (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={handleSelectAll}
          checked={isCheckAll}
        />
      ),
      accessor: '',
      render: (row) => (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          checked={isCheck.includes(row.id)}
        />
      ),

      width: '16px',
    },
    {
      title: 'Image',
      accessor: '',
      width: '110px',
      render: (row) => (
        <img
          src={row.link}
          className="rounded-[10px] w-[108px] h-[70px] object-contain"
        />
      ),
    },
    {
      title: 'Name',
      accessor: 'name',
      render: (row) => {
        const photoName = row.link.substring(row.link.lastIndexOf('/') + 1)
        return photoName
      },
    },
    {
      title: 'Publish Date',
      accessor: 'publishedAt',
      sortable: true,
      render: (row) =>
        row.publishedAt && sanitizeDate(new Date(row.publishedAt)),
    },
    {
      title: 'Published',
      accessor: '',
      render: (row) => (
        <Checkbox
          onCheckedChange={() => {
            if (row.published) {
              handleUnpublish([row.id])
            } else {
              handlePublish([row.id])
            }
          }}
          checked={row.published}
          disabled={!hasEditPermission}
        />
      ),
    },
  ]

  const changePage = async (newPage: number) => {
    setPage(newPage)
  }

  return (
    <>
      <Header
        pageTitle="Media"
        actions={
          <>
            {hasEditPermission && isCheck.length >= 1 && (
              <>
                <ActionsMenu
                  actions={[
                    { label: 'Publish', onClick: () => handlePublish(isCheck) },
                    {
                      label: 'Unpublish',
                      onClick: () => handleUnpublish(isCheck),
                    },
                  ]}
                />
                <Button variant="destructive" onClick={handleDelete}>
                  Delete
                  <Icon
                    icon="close-circle"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                  />
                </Button>
              </>
            )}
            {hasEditPermission && (
              <Button
                variant="primary-bordered"
                onClick={() => {
                  setShowPhotoCreateModal(true)
                  setCurrentPhoto(undefined)
                }}
              >
                Add New
                <Icon
                  icon="plus-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            )}
          </>
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle title="Media" subtitle="Photo Gallery" className="mb-5" />
        <Table
          maxHeight="calc(100svh - 300px)"
          handleSortBy={handleSortBy}
          isCheck={isCheck}
          columns={columns}
          data={photos}
        />
      </Card>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={changePage}
      />
      <MediaPhotoCreateModal
        showCreateModal={showPhotoCreateModal}
        setShowCreateModal={setShowPhotoCreateModal}
        page={page + 1}
        size={size}
        setTotalElemenets={setTotalElemenets}
        setPhotos={setPhotos}
        photo={currentPhoto}
        setLoading={setLoading}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Photo'}
        deleteFunc={deleteFunc}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
